interface Metric {
    value: number;
    labels?: Record<string, string>;
}

interface ParsedMetrics {
    [key: string]: Metric | Metric[] | number;
}

interface DeploymentData {
    headBlock: number;
    delta: number;
    syncSpeed: number;
    eta: number;
}

export interface GraphServerData {
    chainHeadCacheLatestBlock: number;
    deploymentHeads: Record<string, DeploymentData>;
    erpc_upstream_block_head_lag?: number;
    erpc_upstream_finalization_lag?: number;
    erpc_upstream_finalized_block_number?: number;
    erpc_upstream_latest_block_number?: number;
    // Netdata metrics
    netdata_cgroup_cpu_limit_percentage_average?: Array<{
        labels?: {
            cgroup_name?: string;
            dimension?: string;
        };
        value: number;
    }>;
    netdata_ipfs_peers_peers_average?: Array<{
        labels?: {
            dimension?: string;
        };
        value: number;
    }>;
    netdata_cgroup_net_bandwidth_bytes_persec_average?: Array<{
        labels?: {
            cgroup_name?: string;
            dimension?: string;
        };
        value: number;
    }>;
    netdata_ipfs_bandwidth_bytes_persec_average?: Array<{
        labels?: {
            dimension?: string;
        };
        value: number;
    }>;
}

export async function fetchPrometheusMetricsNetdata(url: string): Promise<GraphServerData> {
    try {
        const response = await fetch(url);
        const metrics = await response.text();
        const parsedMetrics = parsePrometheusMetrics(metrics);
        
        let chainHeadCacheLatestBlock = 0;
        const deploymentHeads: Record<string, DeploymentData> = {};
        const syncSpeeds: Record<string, number> = {};
        const processedCounts: Record<string, number> = {};
        
        // Find chain head cache latest block
        chainHeadCacheLatestBlock = (parsedMetrics.chain_head_cache_latest_block as Metric[])[0]!.value;
        
        // Get sync speeds and processed counts for all deployments
        if (Array.isArray(parsedMetrics.deployment_blocks_processed_secs)) {
            parsedMetrics.deployment_blocks_processed_secs.forEach(metric => {
                if (metric.labels?.deployment) {
                    syncSpeeds[metric.labels.deployment] = metric.value;
                }
            });
        }

        if (Array.isArray(parsedMetrics.deployment_blocks_processed_count)) {
            parsedMetrics.deployment_blocks_processed_count.forEach(metric => {
                if (metric.labels?.deployment) {
                    processedCounts[metric.labels.deployment] = metric.value;
                }
            });
        }
        
        // Find all deployment heads
        if (Array.isArray(parsedMetrics.deployment_head)) {
            parsedMetrics.deployment_head.forEach(metric => {
                if (metric.labels?.deployment) {
                    const deploymentId = metric.labels.deployment;
                    const headBlock = metric.value;
                    const delta = chainHeadCacheLatestBlock - headBlock;
                    const blocksProcessed = processedCounts[deploymentId] || 0;
                    const timeSpent = syncSpeeds[deploymentId] || 0;
                    const avgTimePerBlock = blocksProcessed > 0 ? timeSpent / blocksProcessed : 0;
                    const eta = avgTimePerBlock > 0 ? delta * avgTimePerBlock : 0;
                    
                    deploymentHeads[deploymentId] = {
                        headBlock,
                        delta,
                        syncSpeed: avgTimePerBlock,
                        eta
                    };
                }
            });
        }
        
        // Parse RPC proxy metrics
        let erpc_upstream_block_head_lag = 0;
        let erpc_upstream_finalization_lag = 0;
        let erpc_upstream_finalized_block_number = 0;
        let erpc_upstream_latest_block_number = 0;
        
        if (Array.isArray(parsedMetrics.erpc_upstream_block_head_lag)) {
            erpc_upstream_block_head_lag = (parsedMetrics.erpc_upstream_block_head_lag as Metric[])[0]!.value;
        }
        if (Array.isArray(parsedMetrics.erpc_upstream_finalization_lag)) {
            erpc_upstream_finalization_lag = (parsedMetrics.erpc_upstream_finalization_lag as Metric[])[0]!.value;
        }
        if (Array.isArray(parsedMetrics.erpc_upstream_finalized_block_number)) {
            erpc_upstream_finalized_block_number = (parsedMetrics.erpc_upstream_finalized_block_number as Metric[])[0]!.value;
        }
        if (Array.isArray(parsedMetrics.erpc_upstream_latest_block_number)) {
            erpc_upstream_latest_block_number = (parsedMetrics.erpc_upstream_latest_block_number as Metric[])[0]!.value;
        }
        
        return {
            chainHeadCacheLatestBlock,
            deploymentHeads,
            erpc_upstream_block_head_lag,
            erpc_upstream_finalization_lag,
            erpc_upstream_finalized_block_number,
            erpc_upstream_latest_block_number
        };
    } catch (error) {
        console.error('Error fetching Prometheus metrics:', error);
        throw error;
    }
}

function parsePrometheusMetrics(metricsText: string): ParsedMetrics {
    const lines = metricsText.split('\n');
    const parsedMetrics: ParsedMetrics = {};
    
    lines.forEach(line => {
        // Skip comments and empty lines
        if (line.startsWith('#') || line.trim() === '') {
            return;
        }
        
        // Parse metric line
        const [metricName, value] = line.split(' ');
        if (metricName && value) {
            // Handle metrics with labels
            if (metricName.includes('{')) {
                const [name, labels] = metricName.split('{');
                const cleanLabels = labels.replace('}', '');
                const labelPairs = cleanLabels.split(',');
                const labelObject: Record<string, string> = {};
                
                labelPairs.forEach(pair => {
                    const [key, value] = pair.split('=');
                    if (key && value) {
                        labelObject[key] = value.replace(/"/g, '');
                    }
                });
                
                if (!parsedMetrics[name]) {
                    parsedMetrics[name] = [];
                }
                (parsedMetrics[name] as Metric[]).push({
                    value: parseFloat(value),
                    labels: labelObject
                });
            } else {
                parsedMetrics[metricName] = parseFloat(value);
            }
        }
    });
    
    return parsedMetrics;
}

interface NetdataMetric {
    value: number;
    labels?: Record<string, string>;
    timestamp: number;
}

interface ParsedNetdataMetrics {
    [key: string]: NetdataMetric[];
}

interface DiskSpace {
    available: number;
    used: number;
    reserved: number;
    filesystem: string;
    mount_point: string;
    inodes_available: number;
    inodes_used: number;
    inodes_reserved: number;
}

interface DiskStats {
    reads: number;
    writes: number;
    utilization: number;
    busy_time: number;
    backlog: number;
    device: string;
    model?: string;
    mount_point: string;
    device_type: string;
    space?: DiskSpace;
}

interface DockerContainerState {
    name: string;
    image: string;
    state: 'running' | 'paused' | 'exited' | 'created' | 'restarting' | 'removing' | 'dead';
    health: 'healthy' | 'unhealthy' | 'not_running_unhealthy' | 'starting' | 'no_healthcheck';
}

export interface NetdataServerData {
    info?: {
        instance: string;
        application: string;
        version: string;
    };
    docker_containers?: DockerContainerState[];
    system?: {
        cpu_usage: number;
        ram_usage: number;
        ram_total: number;
        load: {
            load1: number;
            load5: number;
            load15: number;
        };
        disks: DiskStats[];
    };
    system_idlejitter?: {
        min: number;
        max: number;
        average: number;
        timestamp: number;
    };
    statsd_metrics?: {
        gauges: number;
        counters: number;
        timers: number;
        meters: number;
        histograms: number;
        sets: number;
        dictionaries: number;
        timestamp: number;
    };
}

export async function fetchNetdataMetrics(url: string): Promise<NetdataServerData> {
    try {
        const response = await fetch(url);
        const metrics = await response.text();
        const parsedMetrics = parseNetdataMetrics(metrics);
        
        // Initialize result object
        const result: NetdataServerData = {};

        // Parse Netdata info
        if (parsedMetrics.netdata_info) {
            const info = parsedMetrics.netdata_info[0];
            if (info?.labels) {
                result.info = {
                    instance: info.labels.instance || '',
                    application: info.labels.application || '',
                    version: info.labels.version || ''
                };
            }
        }

        // Get RAM metrics
        const ramMetrics = parsedMetrics.netdata_system_ram_MiB_average || [];
        
        // Get used and free RAM directly from metrics
        const ramUsed = ramMetrics.find(m => m.labels?.dimension === 'used')?.value || 0;
        const ramFree = ramMetrics.find(m => m.labels?.dimension === 'free')?.value || 0;
        const ramCached = ramMetrics.find(m => m.labels?.dimension === 'cached')?.value || 0;
        const ramBuffers = ramMetrics.find(m => m.labels?.dimension === 'buffers')?.value || 0;
        
        // Calculate total RAM from components
        const ramTotal = ramUsed + ramFree + ramCached + ramBuffers;

        // Get load average metrics
        const loadMetrics = {
            load1: parsedMetrics.netdata_system_load_load_average?.find(m => 
                m.labels?.dimension === 'load1'
            )?.value || 0,
            load5: parsedMetrics.netdata_system_load_load_average?.find(m => 
                m.labels?.dimension === 'load5'
            )?.value || 0,
            load15: parsedMetrics.netdata_system_load_load_average?.find(m => 
                m.labels?.dimension === 'load15'
            )?.value || 0
        };

        // Get all unique disk devices
        const diskDevices = new Set<string>();
        parsedMetrics.netdata_disk_io_KiB_persec_average?.forEach(metric => {
            if (metric.labels?.device) {
                diskDevices.add(metric.labels.device);
            }
        });

        // Collect stats for each disk
        const diskStats: DiskStats[] = [];
        diskDevices.forEach(device => {
            const diskMetrics = {
                reads: parsedMetrics.netdata_disk_io_KiB_persec_average?.find(m => 
                    m.labels?.device === device && m.labels?.dimension === 'reads'
                )?.value || 0,
                writes: Math.abs(parsedMetrics.netdata_disk_io_KiB_persec_average?.find(m => 
                    m.labels?.device === device && m.labels?.dimension === 'writes'
                )?.value || 0),
                utilization: parsedMetrics.netdata_disk_util___of_time_working_average?.find(m => 
                    m.labels?.device === device
                )?.value || 0,
                busy_time: parsedMetrics.netdata_disk_busy_milliseconds_average?.find(m => 
                    m.labels?.device === device
                )?.value || 0,
                backlog: parsedMetrics.netdata_disk_backlog_milliseconds_average?.find(m => 
                    m.labels?.device === device
                )?.value || 0,
                model: parsedMetrics.netdata_disk_io_KiB_persec_average?.find(m => 
                    m.labels?.device === device
                )?.labels?.model,
                mount_point: parsedMetrics.netdata_disk_io_KiB_persec_average?.find(m => 
                    m.labels?.device === device
                )?.labels?.mount_point || '[none]',
                device_type: parsedMetrics.netdata_disk_io_KiB_persec_average?.find(m => 
                    m.labels?.device === device
                )?.labels?.device_type || 'unknown'
            };

            diskStats.push({
                ...diskMetrics,
                device,
                mount_point: diskMetrics.mount_point,
                device_type: diskMetrics.device_type
            });
        });

        // Get disk space information
        const mountPoints = new Set<string>();
        // Add support for both metric name variations
        const diskSpaceMetrics = [
            ...(parsedMetrics.netdata_disk_space_GiB_average || []),
            ...(parsedMetrics.netdata_disk_space_bytes_average || [])
        ];
        
        diskSpaceMetrics.forEach(metric => {
            if (metric.labels?.mount_point) {
                mountPoints.add(metric.labels.mount_point);
            }
        });

        // Add space information to matching disks
        mountPoints.forEach(mount_point => {
            // Try both GiB and bytes metrics
            const spaceMetrics = {
                available: (parsedMetrics.netdata_disk_space_GiB_average?.find(m => 
                    m.labels?.mount_point === mount_point && m.labels?.dimension === 'avail'
                )?.value || 
                (parsedMetrics.netdata_disk_space_bytes_average?.find(m =>
                    m.labels?.mount_point === mount_point && m.labels?.dimension === 'avail'
                )?.value || 0) / (1024 * 1024 * 1024)),
                used: (parsedMetrics.netdata_disk_space_GiB_average?.find(m => 
                    m.labels?.mount_point === mount_point && m.labels?.dimension === 'used'
                )?.value ||
                (parsedMetrics.netdata_disk_space_bytes_average?.find(m =>
                    m.labels?.mount_point === mount_point && m.labels?.dimension === 'used'
                )?.value || 0) / (1024 * 1024 * 1024)),
                reserved: (parsedMetrics.netdata_disk_space_GiB_average?.find(m => 
                    m.labels?.mount_point === mount_point && m.labels?.dimension === 'reserved for root'
                )?.value ||
                (parsedMetrics.netdata_disk_space_bytes_average?.find(m =>
                    m.labels?.mount_point === mount_point && m.labels?.dimension === 'reserved for root'
                )?.value || 0) / (1024 * 1024 * 1024)),
                filesystem: (parsedMetrics.netdata_disk_space_GiB_average?.find(m => 
                    m.labels?.mount_point === mount_point
                )?.labels?.filesystem ||
                parsedMetrics.netdata_disk_space_bytes_average?.find(m =>
                    m.labels?.mount_point === mount_point
                )?.labels?.filesystem || 'unknown'),
                mount_point,
                inodes_available: parsedMetrics.netdata_disk_inodes_inodes_average?.find(m => 
                    m.labels?.mount_point === mount_point && m.labels?.dimension === 'avail'
                )?.value || 0,
                inodes_used: parsedMetrics.netdata_disk_inodes_inodes_average?.find(m => 
                    m.labels?.mount_point === mount_point && m.labels?.dimension === 'used'
                )?.value || 0,
                inodes_reserved: parsedMetrics.netdata_disk_inodes_inodes_average?.find(m => 
                    m.labels?.mount_point === mount_point && m.labels?.dimension === 'reserved for root'
                )?.value || 0
            };

            // Find matching disk and add space information
            const disk = diskStats.find(d => d.mount_point === mount_point);
            if (disk) {
                disk.space = spaceMetrics;
            } else {
                // If no matching disk found, create a new disk entry for this mount point
                diskStats.push({
                    reads: 0,
                    writes: 0,
                    utilization: 0,
                    busy_time: 0,
                    backlog: 0,
                    device: mount_point.replace(/\//g, '') || 'root',
                    mount_point,
                    device_type: 'filesystem',
                    space: spaceMetrics
                });
            }
        });

        // Parse system metrics
        result.system = {
            cpu_usage: parsedMetrics.netdata_system_cpu_percentage_average?.find(m => 
                m.labels?.dimension === 'user' || m.labels?.dimension === 'system'
            )?.value || 0,
            ram_usage: ramUsed,
            ram_total: ramTotal,
            load: loadMetrics,
            disks: diskStats
        };
        // Get Docker container information
        const containerNames = new Set<string>();
        parsedMetrics.netdata_docker_container_state_state_average?.forEach(metric => {
            if (metric.labels?.container_name) {
                containerNames.add(metric.labels.container_name);
            }
        });

        const dockerContainers: DockerContainerState[] = [];
        containerNames.forEach(containerName => {
            // Get container state
            let state: DockerContainerState['state'] = 'exited';
            const stateMetrics = parsedMetrics.netdata_docker_container_state_state_average?.filter(m => 
                m.labels?.container_name === containerName
            ) || [];

            stateMetrics.forEach(metric => {
                if (metric.value === 1 && metric.labels?.dimension) {
                    state = metric.labels.dimension as DockerContainerState['state'];
                }
            });

            // Get container health
            let health: DockerContainerState['health'] = 'no_healthcheck';
            const healthMetrics = parsedMetrics.netdata_docker_container_health_status_status_average?.filter(m => 
                m.labels?.container_name === containerName
            ) || [];

            // Check each health status dimension
            healthMetrics.forEach(metric => {
                if (metric.value === 1 && metric.labels?.dimension) {
                    health = metric.labels.dimension as DockerContainerState['health'];
                }
            });

            // If no health status with value 1 was found, default to no_healthcheck
            if (health === 'no_healthcheck' && healthMetrics.length > 0) {
                // This is already the default, but we keep it for clarity
                health = 'no_healthcheck';
            }

            // Get container image
            const image = stateMetrics[0]?.labels?.image || 'unknown';

            dockerContainers.push({
                name: containerName,
                image,
                state,
                health
            });
        });

        result.docker_containers = dockerContainers;

        return result;
    } catch (error) {
        console.error('Error fetching Netdata metrics:', error);
        throw error;
    }
}

function parseNetdataMetrics(metricsText: string): ParsedNetdataMetrics {
    const lines = metricsText.split('\n');
    const parsedMetrics: ParsedNetdataMetrics = {};
    let currentMetricHelp = '';
    
    lines.forEach(line => {
        // Skip empty lines
        if (line.trim() === '') {
            return;
        }
        
        // Store metric help text
        if (line.startsWith('# HELP ')) {
            currentMetricHelp = line.substring(7);
            return;
        }
        
        // Skip TYPE declarations
        if (line.startsWith('# TYPE ')) {
            return;
        }
        
        // Parse metric line
        if (!line.startsWith('#')) {
            const lastSpace = line.lastIndexOf(' ');
            if (lastSpace === -1) return;
            
            const timestamp = parseFloat(line.substring(lastSpace + 1));
            const valueStartIndex = line.lastIndexOf(' ', lastSpace - 1);
            if (valueStartIndex === -1) return;
            
            const value = parseFloat(line.substring(valueStartIndex + 1, lastSpace));
            const metricPart = line.substring(0, valueStartIndex);
            
            // Parse metric name and labels
            let metricName: string;
            let labels: Record<string, string> = {};
            
            const labelStart = metricPart.indexOf('{');
            if (labelStart !== -1) {
                metricName = metricPart.substring(0, labelStart);
                const labelPart = metricPart.substring(labelStart + 1, metricPart.length - 1);
                labels = labelPart.split(',').reduce((acc: Record<string, string>, curr: string) => {
                    const [key, value] = curr.split('=');
                    if (key && value) {
                        acc[key] = value.replace(/"/g, '');
                    }
                    return acc;
                }, {});
            } else {
                metricName = metricPart;
            }
            
            if (!parsedMetrics[metricName]) {
                parsedMetrics[metricName] = [];
            }
            
            parsedMetrics[metricName].push({
                value,
                labels,
                timestamp
            });
        }
    });
    
    return parsedMetrics;
} 